import { HttpCookieNames, isLocalAuthCookieHost } from './cookie-names';

export const COOKIE_CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing', 'external_media'] as const;

export type CookieConsentCategory = (typeof COOKIE_CONSENT_CATEGORIES)[number];

export const COOKIE_CONSENT_OPTIONAL_CATEGORIES = [
  'analytics',
  'marketing',
  'external_media',
] as const satisfies ReadonlyArray<Exclude<CookieConsentCategory, 'necessary'>>;

export type CookieConsentOptionalCategory = (typeof COOKIE_CONSENT_OPTIONAL_CATEGORIES)[number];

export const COOKIE_CONSENT_REVISION = 1;

export interface CookieConsentRegion {
  countryCode?: string;
  subdivisionCode?: string;
}

const OPT_IN_COUNTRIES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'UK',
  'CH',
]);

/** Call only with server-verified location, never browser locale/timezone or user profile country. */
export function resolveRegionalCookieConsentMode(region?: CookieConsentRegion): 'opt-in' | 'opt-out' {
  const country = region?.countryCode?.trim().toUpperCase();
  if (!(country && /^[A-Z]{2}$/.test(country)) || OPT_IN_COUNTRIES.has(country)) return 'opt-in';
  if (country === 'CA') {
    const subdivision = region?.subdivisionCode?.trim().toUpperCase();
    if (!subdivision || subdivision === 'QC' || subdivision === 'CA-QC') return 'opt-in';
  }
  // Only the explicitly requested US opt-out policy is enabled. Other jurisdictions
  // remain opt-in until their requirements and trusted location mapping are confirmed.
  return country === 'US' ? 'opt-out' : 'opt-in';
}

interface ExplicitCookieConsent {
  categories: string[];
  expirationTime: number;
  revision: number;
}

function isExplicitCookieConsent(value: unknown): value is ExplicitCookieConsent {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const categories = Reflect.get(value, 'categories');
  const expirationTime = Reflect.get(value, 'expirationTime');
  const revision = Reflect.get(value, 'revision');
  return (
    Array.isArray(categories) &&
    categories.every(
      category => typeof category === 'string' && COOKIE_CONSENT_CATEGORIES.some(allowed => allowed === category),
    ) &&
    typeof expirationTime === 'number' &&
    Number.isFinite(expirationTime) &&
    typeof revision === 'number'
  );
}

/** Returns undefined when there is no valid explicit decision to honor. */
export function readExplicitCookieConsentCategory(
  rawConsent: string | undefined,
  category: CookieConsentCategory,
  now = Date.now(),
): boolean | undefined {
  if (!rawConsent) {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawConsent);
  } catch {
    return undefined;
  }

  if (
    !isExplicitCookieConsent(parsed) ||
    parsed.revision !== COOKIE_CONSENT_REVISION ||
    parsed.expirationTime <= now ||
    !parsed.categories.includes('necessary')
  ) {
    return undefined;
  }

  return parsed.categories.includes(category);
}

export type CookieConsentDeployment = 'development' | 'local' | 'production' | 'staging';

const PRODUCTION_COOKIE_DOMAIN = '.starthub.academy';
const STAGING_COOKIE_DOMAIN = '.staging.starthub.academy';
const DEVELOPMENT_COOKIE_DOMAIN = '.dev.starthub.academy';
const STAGING_APEX_HOST = 'staging.starthub.academy';
const DEVELOPMENT_APEX_HOST = 'dev.starthub.academy';

export function resolveCookieConsentDeployment(hostname: string): CookieConsentDeployment {
  if (isLocalAuthCookieHost(hostname)) {
    return 'local';
  }

  if (hostname === STAGING_APEX_HOST || hostname.endsWith(STAGING_COOKIE_DOMAIN)) {
    return 'staging';
  }

  if (hostname === DEVELOPMENT_APEX_HOST || hostname.endsWith(DEVELOPMENT_COOKIE_DOMAIN)) {
    return 'development';
  }

  if (hostname === 'starthub.academy' || hostname.endsWith(PRODUCTION_COOKIE_DOMAIN)) {
    return 'production';
  }

  return 'local';
}

export function getCookieConsentCookieName(hostname: string): string {
  const deployment = resolveCookieConsentDeployment(hostname);
  if (deployment === 'staging') {
    return `${HttpCookieNames.CookieConsent}_staging`;
  }
  if (deployment === 'development') {
    return `${HttpCookieNames.CookieConsent}_dev`;
  }
  return HttpCookieNames.CookieConsent;
}

export function getCookieConsentCookieDomain(hostname: string): string | undefined {
  switch (resolveCookieConsentDeployment(hostname)) {
    case 'production':
      return PRODUCTION_COOKIE_DOMAIN;
    case 'staging':
      return STAGING_COOKIE_DOMAIN;
    case 'development':
      return DEVELOPMENT_COOKIE_DOMAIN;
    default:
      return undefined;
  }
}
